import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../db/firebase";
import { AnimatedModal } from "../../../components/AnimatedModal";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { HiOutlineXMark, HiOutlineMusicalNote } from "react-icons/hi2";

interface AddSongToEventModalProps {
  song: any;
  onClose: () => void;
}

const AddSongToEventModal: React.FC<AddSongToEventModalProps> = ({
  song,
  onClose,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchWorshipEvents = async () => {
      if (!organizationId) {
        setEvents([]);
        return;
      }
      const eventsQuery = query(
        collection(db, "events"),
        where("organizationId", "==", organizationId),
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventsData = eventsSnapshot.docs
        .map((eventDoc) => ({
          id: eventDoc.id,
          ...eventDoc.data(),
        }))
        .filter((event) => event.type === "Worship");
      setEvents(eventsData);
    };

    fetchWorshipEvents();
  }, [organizationId]);

  const handleSave = async () => {
    if (!selectedEvent) {
      setErrorMessage(t("select_event_alert"));
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const event = events.find((event) => event.id === selectedEvent);
      const eventDocRef = doc(db, "events", selectedEvent);

      if (event.songs && event.songs.includes(song.id)) {
        setErrorMessage(t("song_already_in_event", { title: song.title }));
        setLoading(false);
        return;
      }

      await updateDoc(eventDocRef, {
        songs: [...(event.songs || []), song.id],
      });

      alert(t("song_added_to_event", { title: song.title }));
      onClose();
    } catch (error) {
      console.error("Error adding song to event: ", error);
      setErrorMessage(t("error_adding_song"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedModal isOpen onClose={onClose} backdropClassName="bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/80">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineMusicalNote className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("add_song_to_event", { title: song.title })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
          >
            <option value="">{t("select_event")}</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {t("date")}: {new Date(`${event.date}T00:00:00`).toLocaleDateString()}
              </option>
            ))}
          </select>
          {errorMessage && (
            <p className="text-red-500 dark:text-red-400 text-sm">{errorMessage}</p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              className="flex-1 py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] disabled:opacity-50 transition-colors"
              disabled={loading}
            >
              {loading ? t("saving") : t("save")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default AddSongToEventModal;
